/**
 * Pen → muis voor de lijn-bediening op het canvas.
 *
 * Eindpunten, knikpunten en labels van lijnen (RoutedEdge) worden met
 * mousedown/mousemove/mouseup bediend. iPadOS stuurt bij een pen-sleep geen
 * muisgebeurtenissen (alleen pointer- en touch-events; na een tik hooguit
 * samengestelde muis-events), dus slepen met de pen deed daar niets. Deze brug
 * vertaalt een pen-aanraking op een lijn-onderdeel naar muis-events.
 * Vinger en echte muis blijven ongemoeid.
 */

const DOEL = ".react-flow__edge, .react-flow__edgelabel-renderer";

function muis(type: string, x: number, y: number, buttons: number): MouseEvent {
  return new MouseEvent(type, {
    bubbles: true,
    cancelable: true,
    clientX: x,
    clientY: y,
    button: 0,
    buttons,
    view: window,
  });
}

export function installPenMouse(): () => void {
  let start: { target: Element; id: number; x: number; y: number; moved: boolean } | null = null;
  const onTouchStart = (e: TouchEvent) => {
    const t = e.touches[0] as (Touch & { touchType?: string }) | undefined;
    if (t?.touchType !== "stylus") return;
    const el = e.target as Element | null;
    if (!el?.closest(DOEL) || el.closest("input, textarea, [contenteditable='true']")) return;
    // Niet laten pannen/scrollen (d3-zoom) en geen pointercancel door de browser.
    e.stopPropagation();
    if (e.cancelable) e.preventDefault();
  };

  const onDown = (e: PointerEvent) => {
    if (e.pointerType !== "pen") return;
    const el = e.target as Element | null;
    if (!el?.closest(DOEL) || el.closest("input, textarea, [contenteditable='true']")) return;
    start = { target: el, id: e.pointerId, x: e.clientX, y: e.clientY, moved: false };
    el.dispatchEvent(muis("mousedown", e.clientX, e.clientY, 1));
  };

  const onMove = (e: PointerEvent) => {
    if (!start || e.pointerId !== start.id || e.pointerType !== "pen") return;
    if (Math.hypot(e.clientX - start.x, e.clientY - start.y) > 3) start.moved = true;
    e.preventDefault();
    window.dispatchEvent(muis("mousemove", e.clientX, e.clientY, 1));
  };

  const onUp = (e: PointerEvent) => {
    if (!start || e.pointerId !== start.id || e.pointerType !== "pen") return;
    const s = start;
    start = null;
    window.dispatchEvent(muis("mousemove", e.clientX, e.clientY, 1));
    window.dispatchEvent(muis("mouseup", e.clientX, e.clientY, 0));
    if (!s.moved) s.target.dispatchEvent(muis("click", e.clientX, e.clientY, 0));
  };

  const onCancel = () => {
    start = null;
  };

  window.addEventListener("touchstart", onTouchStart, { capture: true, passive: false });
  window.addEventListener("pointerdown", onDown, true);
  window.addEventListener("pointermove", onMove, true);
  window.addEventListener("pointerup", onUp, true);
  window.addEventListener("pointercancel", onCancel, true);
  return () => {
    window.removeEventListener("touchstart", onTouchStart, true);
    window.removeEventListener("pointerdown", onDown, true);
    window.removeEventListener("pointermove", onMove, true);
    window.removeEventListener("pointerup", onUp, true);
    window.removeEventListener("pointercancel", onCancel, true);
  };
}
